import { useMemo } from "react";
import { RotateCcw } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { Badge, Card, KlasseBadge, KpiCard } from "../components/ui";
import { InfoTip } from "../components/Erklaerung";
import { VertragsgrundlageHinweis, VertragsgrundlageTabelle, VertragsgrundlageZuruecksetzen } from "../components/Vertragsgrundlage";
import { useApp } from "../../store/AppStore";
import { resolveIdentity } from "../../domain/identity";
import { identityCoverage } from "../../domain/identityCoverage";
import {
  CLASS_ORDER,
  DEFAULT_RATIO,
  formatEuro,
  formatEuroMio,
  formatFue,
  formatFue1,
  formatNum,
  formatPct,
  formatWeight,
} from "../../domain/rules";
import type { IdentityKey, LicenseClass } from "../../domain/types";

const KEY_LABEL: Record<string, string> = {
  username: "Benutzername",
  personalnummer: "Personalnummer",
  email: "E-Mail",
};
/** Standardschlüssel der App (AppProvider: initialIdentityKey = "personalnummer"). */
const DEFAULT_KEY: IdentityKey = "personalnummer";
const IDENTITY_KEYS: IdentityKey[] = ["personalnummer", "username", "email"];

// Höchste Klasse zuerst, „nicht relevant" ans Ende.
const CLASS_ROWS: LicenseClass[] = [...CLASS_ORDER].reverse();

export function Einstellungen() {
  const { ds, ratio, setRatio, resetRatio, resetAll, ratioIsDefault, price, setPrice, kpis, identity, identityKey, setIdentityKey, contracted } =
    useApp();
  const identityCounts = useMemo(() => ({ username: resolveIdentity(ds.accounts, ds.persons, "username").recognizedPersons, personalnummer: resolveIdentity(ds.accounts, ds.persons, "personalnummer").recognizedPersons }), [ds]);

  // Personen UND FUE je Lizenzklasse aus der AKTIVEN Identitätsauflösung — nicht aus
  // einem festen Datensatz. So summieren die Zeilen exakt auf die Fußzeile (I12).
  const classCounts = identity.byClass;

  // Wirkung je Identitätsschlüssel — ratio-bewusst, damit FUE mit der Tabelle mitrechnet.
  // Abdeckung, Kollisionen, Personen und FUE sind aus den Konten gerechnet, kein Erzähltext.
  const identityRows = useMemo(
    () => IDENTITY_KEYS.map((k) => ({ k, cov: identityCoverage(ds.accounts, ds.persons, k, ratio) })),
    [ds, ratio],
  );

  return (
    <div>
      <PageHeader
        route="/einstellungen"
        right={
          <button
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
            style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-brand-600)" }}
            title="Verhältnistabelle, Preis, Identitätsschlüssel und Warenkorb auf den Auslieferungszustand zurücksetzen"
          >
            <RotateCcw size={14} /> Demo zurücksetzen
          </button>
        }
      />

      <div className="mb-4 rounded-lg border-soft p-4 text-sm" style={{ borderWidth: 1, backgroundColor: "var(--surface-sunken)" }}>
        <span className="text-normal font-medium">Vertragsparameter, keine Konstanten.</span>{" "}
        <span className="text-dim">
          Verhältnistabelle, Vertragsgrundlage, Preis je FUE und Identitätsschlüssel sind die vier Stellschrauben. Jede Änderung rechnet
          den gesamten Bedarf sofort neu — nichts ist im Rechenweg fest verdrahtet.
        </span>
      </div>

      {/* Live-Bedarf als Überschrift der Verhältnistabelle */}
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard label="Bedarf (berechtigungsbasiert)" value={formatFue(kpis.ist)} tone="red" big />
        <KpiCard label="Vertrag (Annahme)" value={formatFue(contracted)} sub="Summe der Vertragsgrundlage je Klasse" tone="brand" big />
        <KpiCard label="Delta" value={`${kpis.delta >= 0 ? "+" : "−"}${formatFue(Math.abs(kpis.delta))}`} sub={`≈ ${formatEuroMio(kpis.deltaEuro)} / Jahr`} tone="red" big />
      </div>

      <Card
        title="Verhältnistabelle"
        subtitle="Gewicht je Klasse in FUE · rechnet live neu"
        right={
          !ratioIsDefault ? (
            <button
              onClick={resetRatio}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
              style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--color-brand-600)" }}
            >
              <RotateCcw size={14} /> Zurücksetzen
            </button>
          ) : (
            <Badge tone="outline">Standardwerte</Badge>
          )
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-soft">
                {["Lizenzklasse", "Gewicht (FUE / Person)", "Standard", "Personen heute (Klasse)", "FUE-Beitrag"].map((h, i) => (
                  <th key={h} className={`text-faint py-2 text-xs font-semibold tracking-wide uppercase ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CLASS_ROWS.map((cls) => {
                const count = classCounts[cls]?.persons ?? 0;
                const contrib = classCounts[cls]?.fue ?? 0;
                const changed = ratio[cls] !== DEFAULT_RATIO[cls];
                return (
                  <tr key={cls} className="border-b border-soft last:border-0">
                    <td className="py-2.5"><KlasseBadge cls={cls} /></td>
                    <td className="py-2.5 text-right">
                      <input
                        type="number"
                        step="any"
                        min={0}
                        value={ratio[cls]}
                        onChange={(e) => {
                          const v = e.target.valueAsNumber;
                          setRatio(cls, Number.isFinite(v) ? v : 0);
                        }}
                        className="tnum bg-card w-28 rounded-md px-2 py-1 text-right text-sm"
                        style={{
                          borderWidth: 1,
                          borderColor: changed ? "var(--color-brand-600)" : "var(--border-soft)",
                          color: "var(--text-strong)",
                        }}
                      />
                    </td>
                    <td className="tnum text-faint py-2.5 text-right">{formatWeight(DEFAULT_RATIO[cls])}</td>
                    <td className="tnum text-dim py-2.5 text-right">{formatNum(count)}</td>
                    <td className="tnum text-normal py-2.5 text-right font-medium">{formatFue1(contrib)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="text-strong py-2.5 font-semibold" colSpan={3}>Summe Bedarf</td>
                <td className="tnum text-dim py-2.5 text-right">{formatNum(identity.recognizedPersons)}</td>
                <td className="tnum py-2.5 text-right font-semibold" style={{ color: "var(--color-amp-red)" }}>{formatFue(kpis.ist)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Vertragsgrundlage je Klasse — Eingabe, Summe ist die Vertragszahl (gleicher Store wie die Vertragsseite). */}
      <Card
        title={<span className="inline-flex items-center gap-1">Vertragsgrundlage <VertragsgrundlageHinweis /></span>}
        subtitle="angenommene Personen je Klasse · Summe = Vertragszahl · rechnet live neu"
        className="mt-6"
        right={<VertragsgrundlageZuruecksetzen />}
      >
        <VertragsgrundlageTabelle />
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Preis je FUE" subtitle="bestimmt den Euro-Wert des Deltas">
          <label className="text-dim block text-sm font-medium">Preis je FUE (€ / Jahr)</label>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              type="number"
              step="any"
              min={0}
              value={price}
              onChange={(e) => {
                const v = e.target.valueAsNumber;
                setPrice(Number.isFinite(v) ? v : 0);
              }}
              className="tnum bg-card w-40 rounded-md px-3 py-2 text-right text-sm"
              style={{ borderWidth: 1, borderColor: "var(--border-soft)", color: "var(--text-strong)" }}
            />
            <span className="text-faint text-sm">€ / FUE</span>
          </div>
          <div className="mt-4 rounded-lg p-3" style={{ backgroundColor: "var(--surface-sunken)" }}>
            <div className="text-dim text-[13px]">Delta {formatFue(kpis.delta)} × {formatNum(price)} € ergibt</div>
            <div className="tnum mt-1 text-2xl font-semibold" style={{ color: "var(--color-amp-red)" }}>{formatEuro(kpis.deltaEuro)} / Jahr</div>
            <div className="text-faint mt-1 text-xs">≈ {formatEuroMio(kpis.deltaEuro)} / Jahr</div>
          </div>
        </Card>

        <Card title="Datensatzgröße" subtitle="nicht editierbar">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-dim text-sm">Personen-Stammsatz (dedupliziert)</span>
              <span className="tnum text-strong text-sm font-semibold">{formatNum(ds.persons.length)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-dim text-sm">Erkannte Einheiten ({KEY_LABEL[identityKey] ?? identityKey})</span>
              <span className="tnum text-strong text-sm font-semibold">{formatNum(identity.recognizedPersons)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-dim text-sm">Konten (roh)</span>
              <span className="tnum text-strong text-sm font-semibold">{formatNum(ds.accounts.length)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-dim text-sm">Technische User (nicht gezählt)</span>
              <span className="tnum text-dim text-sm font-semibold">{formatNum(ds.technicalCount)}</span>
            </div>
          </div>
        </Card>
      </div>

      <Card
        title="Identitätsschlüssel"
        subtitle="Umschalten rechnet Personen und FUE neu"
        className="mt-6"
      >
        <div className="text-dim mb-3 text-sm">
          Aktiver Schlüssel: <span className="text-normal font-medium">{KEY_LABEL[identityKey] ?? identityKey}</span>. Ein
          Kernfeature — kein Detail: Benutzername erkennt {formatNum(identityCounts.username)} Personen, Personalnummer {formatNum(identityCounts.personalnummer)}.
        </div>
        <div className="space-y-2">
          {identityRows.map(({ k, cov }) => {
            const active = k === identityKey;
            return (
              <label
                key={k}
                className="flex cursor-pointer items-center gap-3 rounded-lg p-3"
                style={{
                  borderWidth: 1,
                  borderColor: active ? "var(--color-brand-600)" : "var(--border-soft)",
                  backgroundColor: active ? "var(--surface-sunken)" : "transparent",
                }}
              >
                <input
                  type="radio"
                  name="identityKey"
                  checked={active}
                  onChange={() => setIdentityKey(k)}
                  className="shrink-0"
                  style={{ accentColor: "var(--color-brand-600)" }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-strong text-sm font-semibold">{KEY_LABEL[k]}</span>
                    {k === DEFAULT_KEY && <Badge tone="outline">Standard</Badge>}
                    {active && <Badge tone="brand">aktiv</Badge>}
                  </div>
                  <div className="text-faint tnum text-xs">
                    Abdeckung {formatPct(cov.coveragePct, 1)} der fachlichen Konten ({formatNum(cov.withKey)} von {formatNum(cov.accounts)}) · Kollisionen {formatNum(cov.collisions)}
                    {cov.collisions > 0 && <> ({formatNum(cov.collisionAccounts)} Konten)</>}
                    <InfoTip size={12}>Abdeckung = Konten mit Wert im Schlüsselfeld / fachliche Konten. Kollision = ein Schlüsselwert zeigt auf mehrere Personen; diese Konten werden nicht zusammengeführt und zählen einzeln (I13/I14). Konten ohne Wert zählen ebenfalls einzeln.</InfoTip>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="tnum text-normal text-sm font-semibold">{formatNum(cov.recognizedPersons)} Personen</div>
                  <div className="tnum text-dim text-xs">{formatFue(cov.fueTotal)}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="text-faint mt-3 text-xs">
          Fehlt der Schlüssel oder ist er mehrdeutig, wird nicht zusammengeführt — die Person zählt einzeln. Das ergibt
          bewusst eine zu hohe Zahl (konservativ).
        </div>
      </Card>
    </div>
  );
}
